import { useState, useEffect, useRef } from "react";
import { useMatchingBancarioFiltersStore } from "../zustand/filters.store.js";
import { useFilterOptions } from "./useFilterOptions.js";
import { parseARtoISO, isFechaFutura } from "../../../common/utils/functions/dateUtils.js";

export const useModalFilter = () => {
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [resetKey, setResetKey] = useState(0);

    // Obtener estado y acciones del store
    const { banco, fechaDesde, fechaHasta, fechaEstadoDesde, fechaEstadoHasta, estado } = useMatchingBancarioFiltersStore((state) => state.filters);
    const setFilters = useMatchingBancarioFiltersStore((state) => state.setFilters);

    // Referencia a filtros aplicados
    const filtrosAplicadosRef = useRef({
        banco: null,
        fechaDesde: null,
        fechaHasta: null,
        fechaEstadoDesde: null,
        fechaEstadoHasta: null,
        estado: null
    });

    // Cargar opciones de filtros (bancos y estados) desde hook centralizado
    const { bancos, estados, bancosLoading, estadosLoading, getBancoLabel, getEstadoLabel } = useFilterOptions();

    const convertirFechaParaFormulario = (fechaStr) => {
        if (!fechaStr || fechaStr.includes('/')) return fechaStr;

        const partes = fechaStr.split('-');
        if (partes.length === 3) {
            const [anio, mes, dia] = partes;
            return `${dia}/${mes}/${anio}`;
        }
        return fechaStr;
    };

    // Estados locales del formulario
    const [bancoSelected, setBancoSelected] = useState(banco || null);
    const [bancoSelectedLabel, setBancoSelectedLabel] = useState(
        banco ? getBancoLabel(banco) : ""
    );
    const [fechaDesdeInput, setFechaDesdeInput] = useState(
        fechaDesde ? convertirFechaParaFormulario(fechaDesde) : ""
    );
    const [fechaHastaInput, setFechaHastaInput] = useState(
        fechaHasta ? convertirFechaParaFormulario(fechaHasta) : ""
    );
    const [fechaEstadoDesdeInput, setFechaEstadoDesdeInput] = useState(
        fechaEstadoDesde ? convertirFechaParaFormulario(fechaEstadoDesde) : ""
    );
    const [fechaEstadoHastaInput, setFechaEstadoHastaInput] = useState(
        fechaEstadoHasta ? convertirFechaParaFormulario(fechaEstadoHasta) : ""
    );
    const [estadoSelected, setEstadoSelected] = useState(estado || null);
    const [estadoSelectedLabel, setEstadoSelectedLabel] = useState(
        estado ? getEstadoLabel(estado) : ""
    );
    const [errorFechaDesde, setErrorFechaDesde] = useState("");
    const [errorFechaHasta, setErrorFechaHasta] = useState("");
    const [errorFechaEstadoDesde, setErrorFechaEstadoDesde] = useState("");
    const [errorFechaEstadoHasta, setErrorFechaEstadoHasta] = useState("");
    const [warningFechaDesde, setWarningFechaDesde] = useState("");

    // Sincronizar con el store (sin incluir bancos/estados para evitar re-renders)
    useEffect(() => {
        const fechaDesdeConvertida = fechaDesde ? convertirFechaParaFormulario(fechaDesde) : "";
        const fechaHastaConvertida = fechaHasta ? convertirFechaParaFormulario(fechaHasta) : "";
        const fechaEstadoDesdeConvertida = fechaEstadoDesde ? convertirFechaParaFormulario(fechaEstadoDesde) : "";
        const fechaEstadoHastaConvertida = fechaEstadoHasta ? convertirFechaParaFormulario(fechaEstadoHasta) : "";

        filtrosAplicadosRef.current = {
            banco,
            fechaDesde: fechaDesdeConvertida,
            fechaHasta: fechaHastaConvertida,
            fechaEstadoDesde: fechaEstadoDesdeConvertida,
            fechaEstadoHasta: fechaEstadoHastaConvertida,
            estado
        };

        setBancoSelected(prev => prev !== banco ? banco : prev);
        setFechaDesdeInput(prev => prev !== fechaDesdeConvertida ? fechaDesdeConvertida : prev);
        setFechaHastaInput(prev => prev !== fechaHastaConvertida ? fechaHastaConvertida : prev);
        setFechaEstadoDesdeInput(prev => prev !== fechaEstadoDesdeConvertida ? fechaEstadoDesdeConvertida : prev);
        setFechaEstadoHastaInput(prev => prev !== fechaEstadoHastaConvertida ? fechaEstadoHastaConvertida : prev);
        setEstadoSelected(prev => prev !== estado ? estado : prev);
        setErrorFechaDesde("");
        setErrorFechaHasta("");
        setErrorFechaEstadoDesde("");
        setErrorFechaEstadoHasta("");
        setWarningFechaDesde("");
    }, [banco, fechaDesde, fechaHasta, fechaEstadoDesde, fechaEstadoHasta, estado]);

    // Actualizar label de banco cuando cambia la selección o los datos
    useEffect(() => {
        setBancoSelectedLabel(bancoSelected ? getBancoLabel(bancoSelected) : "");
    }, [bancoSelected, getBancoLabel]);

    // Actualizar label de estado cuando cambia la selección o los datos
    useEffect(() => {
        setEstadoSelectedLabel(estadoSelected ? getEstadoLabel(estadoSelected) : "");
    }, [estadoSelected, getEstadoLabel]);

    // Validaciones de fecha
    const esFechaValida = (fechaStr) => {
        if (!fechaStr?.length || fechaStr.length !== 10) return false;

        const partes = fechaStr.split('/');
        if (partes.length !== 3) return false;

        const [dia, mes, anio] = partes.map(p => Number.parseInt(p, 10));

        if (Number.isNaN(dia) || Number.isNaN(mes) || Number.isNaN(anio)) return false;
        if (mes < 1 || mes > 12) return false;
        if (dia < 1) return false;

        const diasPorMes = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
        const esBisiesto = (anio % 4 === 0 && anio % 100 !== 0) || (anio % 400 === 0);
        if (esBisiesto && mes === 2) {
            diasPorMes[1] = 29;
        }

        if (dia > diasPorMes[mes - 1]) return false;

        return true;
    };

    const fechaTieneErrorPropio = (fechaStr) => {
        if (fechaStr?.length !== 10) return false;

        const formatoInvalido = !esFechaValida(fechaStr);
        const esFutura = esFechaValida(fechaStr) && isFechaFutura(fechaStr);

        return formatoInvalido || esFutura;
    };

    // Handlers
    const handleBancoChange = (value) => {
        if (value === "") {
            setBancoSelected(null);
            setBancoSelectedLabel("");
            return;
        }

        // Buscar el banco por value (ID) o label (nombre)
        const bancoEncontrado = bancos.find(b =>
            b.value.toString() === value || b.label === value
        );

        if (bancoEncontrado) {
            setBancoSelected(bancoEncontrado.value);  // ID numérico
            setBancoSelectedLabel(bancoEncontrado.label);  // Nombre
        }
    };

    const handleEstadoChange = (value) => {
        if (value === "") {
            setEstadoSelected(null);
            setEstadoSelectedLabel("");
            return;
        }

        // El value puede venir como string numérico ("5") o como label ("Confirmado")
        // Intentamos encontrar el estado por value primero
        const estadoEncontrado = estados.find(e => e.value.toString() === value || e.label === value);

        if (estadoEncontrado) {
            setEstadoSelected(estadoEncontrado.value);
            setEstadoSelectedLabel(estadoEncontrado.label);
        }
    };

    const validarFechaDesde = (value, fechaHastaInput, setErrorDesde, setErrorHasta, mensajeRango) => {
        if (value?.length !== 10) {
            setErrorDesde("");
            if (!fechaTieneErrorPropio(fechaHastaInput)) setErrorHasta("");
            return;
        }

        if (!esFechaValida(value)) {
            setErrorDesde("Ingresá una fecha válida");
            return;
        }

        if (isFechaFutura(value)) {
            setErrorDesde("La fecha no puede ser futura");
            return;
        }

        setErrorDesde("");
        if (fechaHastaInput?.length !== 10 || !esFechaValida(fechaHastaInput)) return;

        const fechaDesdeISO = parseARtoISO(value);
        const fechaHastaISO = parseARtoISO(fechaHastaInput);
        if (fechaDesdeISO && fechaHastaISO && fechaHastaISO < fechaDesdeISO) {
            setErrorHasta(mensajeRango);
        } else if (!fechaTieneErrorPropio(fechaHastaInput)) {
            setErrorHasta("");
        }
    };

    const validarFechaHasta = (value, fechaDesdeInput, setError, mensajeRango) => {
        if (value?.length !== 10) {
            setError("");
            return;
        }

        if (!esFechaValida(value)) {
            setError("Ingresá una fecha válida");
            return;
        }

        if (isFechaFutura(value)) {
            setError("La fecha no puede ser futura");
            return;
        }

        setError("");
        if (fechaDesdeInput?.length !== 10 || !esFechaValida(fechaDesdeInput)) return;

        const fechaDesdeISO = parseARtoISO(fechaDesdeInput);
        const fechaHastaISO = parseARtoISO(value);
        if (fechaDesdeISO && fechaHastaISO && fechaHastaISO < fechaDesdeISO) {
            setError(mensajeRango);
        }
    };

    const handleFechaDesdeChange = (value) => {
        setFechaDesdeInput(value);

        if (!value) {
            setWarningFechaDesde("Sin \"fecha mail desde\" se puede obtener una gran cantidad de registros");
        } else {
            setWarningFechaDesde("");
        }

        validarFechaDesde(
            value,
            fechaHastaInput,
            setErrorFechaDesde,
            setErrorFechaHasta,
            "La \"fecha mail hasta\" no puede ser anterior a la \"fecha mail desde\""
        );
    };

    const handleFechaHastaChange = (value) => {
        setFechaHastaInput(value);
        validarFechaHasta(
            value,
            fechaDesdeInput,
            setErrorFechaHasta,
            "La \"fecha mail hasta\" no puede ser anterior a la \"fecha mail desde\""
        );
    };

    const handleFechaEstadoDesdeChange = (value) => {
        setFechaEstadoDesdeInput(value);
        validarFechaDesde(
            value,
            fechaEstadoHastaInput,
            setErrorFechaEstadoDesde,
            setErrorFechaEstadoHasta,
            "La \"fecha estado hasta\" no puede ser anterior a la \"fecha estado desde\""
        );
    };

    const handleFechaEstadoHastaChange = (value) => {
        setFechaEstadoHastaInput(value);
        validarFechaHasta(
            value,
            fechaEstadoDesdeInput,
            setErrorFechaEstadoHasta,
            "La \"fecha estado hasta\" no puede ser anterior a la \"fecha estado desde\""
        );
    };

    const handleAccept = () => {
        // Crear objeto con todos los filtros (null para los que no están seleccionados)
        const filtrosNuevos = {
            banco: bancoSelected || null,
            fechaDesde: (fechaDesdeInput?.length === 10 && esFechaValida(fechaDesdeInput))
                ? parseARtoISO(fechaDesdeInput)
                : null,
            fechaHasta: (fechaHastaInput?.length === 10 && esFechaValida(fechaHastaInput))
                ? parseARtoISO(fechaHastaInput)
                : null,
            fechaEstadoDesde: (fechaEstadoDesdeInput?.length === 10 && esFechaValida(fechaEstadoDesdeInput))
                ? parseARtoISO(fechaEstadoDesdeInput)
                : null,
            fechaEstadoHasta: (fechaEstadoHastaInput?.length === 10 && esFechaValida(fechaEstadoHastaInput))
                ? parseARtoISO(fechaEstadoHastaInput)
                : null,
            estado: estadoSelected || null
        };

        setFilters(filtrosNuevos);
        setIsFilterModalOpen(false);
    };

    const handleClear = () => {
        setBancoSelected(null);
        setBancoSelectedLabel("");
        setFechaDesdeInput("");
        setFechaHastaInput("");
        setFechaEstadoDesdeInput("");
        setFechaEstadoHastaInput("");
        setEstadoSelected(null);
        setEstadoSelectedLabel("");
        setErrorFechaDesde("");
        setErrorFechaHasta("");
        setErrorFechaEstadoDesde("");
        setErrorFechaEstadoHasta("");
        setWarningFechaDesde("Sin \"fecha mail desde\" se puede obtener una gran cantidad de registros");
        setResetKey(prev => prev + 1);
    };

    const restaurarFiltrosAplicados = () => {
        const bancoRestaurado = filtrosAplicadosRef.current.banco;
        const estadoRestaurado = filtrosAplicadosRef.current.estado;

        setBancoSelected(bancoRestaurado);
        setBancoSelectedLabel(bancoRestaurado ? getBancoLabel(bancoRestaurado) : "");
        setFechaDesdeInput(filtrosAplicadosRef.current.fechaDesde);
        setFechaHastaInput(filtrosAplicadosRef.current.fechaHasta);
        setFechaEstadoDesdeInput(filtrosAplicadosRef.current.fechaEstadoDesde);
        setFechaEstadoHastaInput(filtrosAplicadosRef.current.fechaEstadoHasta);
        setEstadoSelected(estadoRestaurado);
        setEstadoSelectedLabel(estadoRestaurado ? getEstadoLabel(estadoRestaurado) : "");
        setErrorFechaDesde("");
        setErrorFechaHasta("");
        setErrorFechaEstadoDesde("");
        setErrorFechaEstadoHasta("");
        setWarningFechaDesde("");
    };

    return {
        // Estados
        isFilterModalOpen,
        setIsFilterModalOpen,
        resetKey,
        bancos,
        estados,
        bancosLoading,
        estadosLoading,

        // Estados del formulario
        bancoSelected,
        bancoSelectedLabel,
        fechaDesdeInput,
        fechaHastaInput,
        fechaEstadoDesdeInput,
        fechaEstadoHastaInput,
        estadoSelected,
        estadoSelectedLabel,
        errorFechaDesde,
        errorFechaHasta,
        errorFechaEstadoDesde,
        errorFechaEstadoHasta,
        warningFechaDesde,

        // Handlers
        handleAccept,
        handleClear,
        handleBancoChange,
        handleEstadoChange,
        handleFechaDesdeChange,
        handleFechaHastaChange,
        handleFechaEstadoDesdeChange,
        handleFechaEstadoHastaChange,
        restaurarFiltrosAplicados,

        // Helper para obtener label
        getEstadoLabel,
    };
};
